import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BiSave, BiX } from 'react-icons/bi';

import Bouton from '../../UI/Bouton/Bouton';
import TaskBasicInfo from './TaskBasicInfo';
import TaskPlanningSection from './TaskPlanningSection';
import TaskExecutionSection from './TaskExecutionSection';
import TaskFunctionSection from './TaskFunctionSection';
import Collapse from '../../../hoc/Collapse/Collapse';

import { generateId } from '../../../ressources/functionJS/generateId';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import { getFunctionById, getFunctions } from '../../../services/api';
import S from '../ProfilTasks/FormulaireTasks/FormulaireTasks.module.scss';

const TaskBuilder = ({
  initialTask = {},
  isEditMode = false,
  annuler,
  sauvegarderTache,
  readOnly = false,
}) => {
  const [taskData, setTaskData] = useState({
    id: '',
    nom: '',
    active: true,
    mode: 'manual',
    frequence: '',
    interval_years: 0,
    interval_months: 0,
    interval_weeks: 0,
    interval_days: 0,
    interval_hours: 0,
    interval_minutes: 0,
    interval_seconds: 0,
    frequence_date: '',
    frequence_time: '',
    end_date: '',
    end_time: '',
    days_of_week: '',
    fonction: null,
  });
  const [erreurs, setErreurs] = useState({});
  const [planningContext, setPlanningContext] = useState({
    project_id: '',
    view_id: '',
    group_taches: 'tasks_group',
    viewType: '',
  });
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [loadingFunctions, setLoadingFunctions] = useState(false);

  // Load available functions from API
  useEffect(() => {
    const loadFunctions = async () => {
      setLoadingFunctions(true);
      try {
        const response = await getFunctions();
        // La réponse a la structure: { status: "OK", data: [...], count: number }
        let functionsArray = [];
        if (response && response.data && Array.isArray(response.data)) {
          functionsArray = response.data;
        } else if (response && Array.isArray(response)) {
          functionsArray = response;
        } else if (
          response &&
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          functionsArray = response.data.data;
        }
        if (functionsArray.length > 0) {
          const mappedFunctions = functionsArray.map((fn) => ({
            value: fn.id_function || fn.id,
            label: fn.name || fn.nom || 'Sans nom',
          }));
          setAvailableFunctions(mappedFunctions);
        } else {
          console.warn('No functions found in response');
        }
      } catch (error) {
        console.error('Error loading functions:', error);
      } finally {
        setLoadingFunctions(false);
      }
    };
    loadFunctions();
  }, []);

  useEffect(() => {
    // In read-only mode we still want to hydrate from the provided task
    if ((isEditMode || readOnly) && initialTask?.task_id) {
      const cronTime = `${initialTask.cron_hour}:${
        initialTask.cron_minute?.padStart(2, '0') || '00'
      }`;
      // Gérer les 3 types: manual, planned, scheduled
      const mode =
        initialTask.type === 'scheduled_task' || initialTask.type === 'planned'
          ? 'scheduled'
          : 'manual';

      const interval_years_val =
        initialTask.cron_year && initialTask.cron_year !== '*'
          ? parseInt(initialTask.cron_year, 10) || 0
          : 0;
      const interval_months_val =
        initialTask.cron_month && initialTask.cron_month !== '*'
          ? parseInt(initialTask.cron_month, 10) || 0
          : 0;
      const interval_weeks_val =
        initialTask.cron_week && initialTask.cron_week !== '*'
          ? parseInt(initialTask.cron_week, 10) || 0
          : 0;
      const interval_days_val =
        initialTask.cron_day && initialTask.cron_day !== '*'
          ? parseInt(initialTask.cron_day, 10) || 0
          : 0;
      const interval_hours_val =
        initialTask.cron_hour && initialTask.cron_hour !== '*'
          ? parseInt(initialTask.cron_hour, 10) || 0
          : 0;
      const interval_minutes_val =
        initialTask.cron_minute && initialTask.cron_minute !== '*'
          ? parseInt(initialTask.cron_minute, 10) || 0
          : 0;
      const interval_seconds_val =
        initialTask.cron_second && initialTask.cron_second !== '*'
          ? parseInt(initialTask.cron_second, 10) || 0
          : 0;

      // Détecter si c'est une exécution unique (planned) ou récurrente (scheduled)
      const isPlanned = initialTask.type === 'planned';
      const isSingleExecution =
        initialTask.cron_year !== '*' &&
        initialTask.cron_month !== '*' &&
        initialTask.cron_day !== '*';

      // Détecter si c'est une fréquence basée sur les jours de la semaine
      const hasDaysOfWeek =
        initialTask.cron_day_of_week &&
        initialTask.cron_day_of_week !== '*' &&
        initialTask.cron_day_of_week !== '';

      let frequence = '';
      if (mode === 'scheduled') {
        if (hasDaysOfWeek) {
          // Jours de la semaine
          frequence = 'days_of_week';
        } else if (isPlanned || isSingleExecution) {
          // Exécution unique
          frequence = 'once';
        } else if (
          interval_years_val > 0 ||
          interval_months_val > 0 ||
          interval_weeks_val > 0 ||
          interval_days_val > 0 ||
          interval_hours_val > 0 ||
          interval_minutes_val > 0 ||
          interval_seconds_val > 0
        ) {
          // Intervalle personnalisé
          frequence = 'custom';
        }
      }

      setTaskData({
        id: initialTask.task_id,
        nom: initialTask.name || '',
        active:
          initialTask.state !== undefined ? initialTask.state !== 0 : true,
        mode: mode,
        frequence: frequence,
        interval_years: interval_years_val,
        interval_months: interval_months_val,
        interval_weeks: interval_weeks_val,
        interval_days: interval_days_val,
        interval_hours: interval_hours_val,
        interval_minutes: interval_minutes_val,
        interval_seconds: interval_seconds_val,
        frequence_date: initialTask.cron_start_day || '',
        frequence_time: cronTime,
        end_date: initialTask.cron_end_date || '',
        end_time: cronTime,
        days_of_week:
          initialTask.cron_day_of_week && initialTask.cron_day_of_week !== '*'
            ? initialTask.cron_day_of_week
            : '',
        fonction: {
          id: generateId(),
          function_id: initialTask.function_id,
          nom: initialTask.function_name || '',
          loading: false,
          arguments: (initialTask.id_arguments || []).map((arg, idx) => ({
            id: generateId(),
            position: arg.Position_in_arg || idx + 1,
            nom: `Argument ${arg.Position_in_arg}`,
            type: 'text',
            obligatoire: true,
            description: '',
            valeurParDefaut: arg.Value || '',
            value: arg.Value || '',
            options: [],
          })),
        },
      });

      setPlanningContext({
        project_id: initialTask.project_id || '',
        view_id: initialTask.view_id || '',
        group_taches: initialTask.group_taches || 'tasks_group',
        viewType: '',
      });

      if (initialTask.function_id) {
        loadFunctionDetailsForEdit(
          initialTask.function_id,
          initialTask.id_arguments
        );
      }
    } else {
      setTaskData({
        id: generateId(),
        nom: '',
        active: true,
        mode: 'manual',
        frequence: '',
        interval_years: 0,
        interval_months: 0,
        interval_weeks: 0,
        interval_days: 0,
        interval_hours: 0,
        interval_minutes: 0,
        interval_seconds: 0,
        frequence_date: '',
        frequence_time: '',
        end_date: '',
        end_time: '',
        days_of_week: '',
        fonction: null,
      });
    }
  }, [initialTask, isEditMode, readOnly]);

  // Load function details for edit mode and merge with existing argument values
  const loadFunctionDetailsForEdit = async (functionId, existingArguments) => {
    try {
      const functionData = await getFunctionById({ functionId });

      if (
        functionData?.data?.arguments_definition &&
        Array.isArray(functionData.data.arguments_definition)
      ) {
        const apiArguments = functionData.data.arguments_definition.map(
          (arg, idx) => {
            // Find matching existing argument value
            const existingArg = existingArguments?.find(
              (ea) =>
                ea.Position_in_arg === (arg.position_in_arg_list || idx + 1)
            );

            return {
              id: generateId(),
              position: arg.position_in_arg_list || idx + 1,
              nom: arg.title || arg.type || '',
              type: arg.type || 'text',
              obligatoire: true,
              description: arg.description || '',
              valeurParDefaut: existingArg?.Value || '',
              value: existingArg?.Value || '',
              options: arg.options || [],
              apiData: arg,
            };
          }
        );

        setTaskData((prev) => ({
          ...prev,
          fonction: {
            ...prev.fonction,
            arguments: apiArguments,
            nom: functionData.data.name || prev.fonction?.nom || '',
            description: functionData.data.description || '',
          },
        }));
      }
    } catch (error) {
      console.error('Error loading function details for edit:', error);
    }
  };

  // Memoized handlers
  const handleTaskChange = useCallback(
    (field, value) => {
      if (readOnly) return;
      setTaskData((prev) => ({ ...prev, [field]: value }));
      setErreurs((prev) => {
        if (prev[field]) {
          const { [field]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [readOnly]
  );

  const handlePlanningChange = useCallback(
    (field, value) => {
      if (readOnly) return;
      setPlanningContext((prev) => ({ ...prev, [field]: value }));
    },
    [readOnly]
  );

  useEffect(() => {
    // Ne plus forcer le passage en mode manuel pour 'once'.
    // Si une fréquence est définie (quelque soit sa valeur), on bascule en 'scheduled'
    // sauf si l'utilisateur a explicitement choisi 'manual'.
    if (taskData.frequence && taskData.mode !== 'manual') {
      setTaskData((prev) => ({ ...prev, mode: 'scheduled' }));
    }
  }, [taskData.frequence]);

  const handleFonctionChange = async (field, value) => {
    if (readOnly) return;
    setTaskData((prev) => ({
      ...prev,
      fonction: prev.fonction
        ? { ...prev.fonction, [field]: value }
        : { id: generateId(), [field]: value, arguments: [] },
    }));

    // If function_id changed, call getFunctionById and load arguments
    if (field === 'function_id' && value) {
      try {
        // Mark function as loading
        setTaskData((prev) => ({
          ...prev,
          fonction: {
            ...(prev.fonction || {}),
            id: prev.fonction?.id || generateId(),
            function_id: value,
            loading: true,
            arguments: [],
          },
        }));

        const functionData = await getFunctionById({ functionId: value });
        // Extract arguments from the response
        if (
          functionData?.data?.arguments_definition &&
          Array.isArray(functionData.data.arguments_definition)
        ) {
          const apiArguments = functionData.data.arguments_definition.map(
            (arg, idx) => ({
              id: generateId(),
              position: arg.position_in_arg_list || idx + 1,
              nom: arg.title || arg.type || '',
              type: arg.type || 'text',
              obligatoire: true,
              description: arg.description || '',
              valeurParDefaut: '',
              options: arg.options || [],
              // Keep the raw API data for reference
              apiData: arg,
            })
          );

          const selectedFunction = availableFunctions.find(
            (fn) => fn.value === value
          );

          setTaskData((prev) => ({
            ...prev,
            fonction: {
              ...prev.fonction,
              arguments: apiArguments,
              loading: false,
              nom: functionData.data.name || selectedFunction?.label || '',
              description: functionData.data.description || '',
              function_id: value,
            },
          }));
        } else {
          setTaskData((prev) => ({
            ...prev,
            fonction: {
              ...prev.fonction,
              loading: false,
              arguments: [],
            },
          }));
        }
      } catch (error) {
        console.error('Error calling getFunctionById:', error);
        setTaskData((prev) => ({
          ...prev,
          fonction: prev.fonction ? { ...prev.fonction, loading: false } : null,
        }));
      }
    }
  };

  const handleArgumentChange = useCallback(
    (indexArg, field, value) => {
      if (readOnly) return;
      setTaskData((prev) => ({
        ...prev,
        fonction: prev.fonction
          ? {
              ...prev.fonction,
              arguments: (prev.fonction.arguments || []).map((a, j) =>
                j === indexArg ? { ...a, [field]: value } : a
              ),
            }
          : null,
      }));
    },
    [readOnly]
  );

  const valider = useCallback(() => {
    if (readOnly) return true;
    const errs = {};
    if (!taskData.nom) errs.nom = 'Nom de la tâche requis';
    if (!taskData.fonction || !taskData.fonction.function_id) {
      errs.fonction = 'Sélectionnez une fonction';
    }
    setErreurs(errs);
    return Object.keys(errs).length === 0;
  }, [taskData.nom, taskData.fonction]);

  const handleSave = () => {
    if (readOnly) return;
    // if (!valider()) return;

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    };

    let task;

    if (taskData.mode === 'manual') {
      const now = new Date();
      const startDate = new Date(now.getTime() + 5 * 60000); // +5 minutes

      task = {
        name: taskData.nom,
        type: 'manual',
        function_id: taskData.fonction?.function_id,
        group_taches: planningContext.group_taches || 'tasks_group',
        cron_year: '*',
        cron_month: '*',
        cron_day: '*',
        cron_week: '*',
        cron_day_of_week: '*',
        cron_hour: '*',
        cron_minute: '*',
        cron_second: '*',
        cron_start_day: formatDate(startDate),
        cron_end_date: '*',
        project_id: planningContext.project_id
          ? parseInt(planningContext.project_id, 10)
          : undefined,
        view_id: planningContext.view_id
          ? parseInt(planningContext.view_id, 10)
          : undefined,
        state: taskData.active ? 2 : 0,
        id_arguments: (taskData.fonction?.arguments || []).map((arg) => ({
          Position_in_arg: arg.position,
          Value: arg.valeurParDefaut || arg.value || '',
        })),
      };
    } else {
      // Tâche planifiée : state = 1, cron_ représente la fréquence
      const startDate = taskData.frequence_date
        ? new Date(taskData.frequence_date)
        : new Date();
      let endDate = taskData.end_date
        ? new Date(taskData.end_date)
        : new Date(new Date().setFullYear(new Date().getFullYear() + 1));

      // Déterminer les valeurs CRON selon la fréquence
      let cronYear = '*';
      let cronMonth = '*';
      let cronDay = '*';
      let cronWeek = '*';
      let cronDayOfWeek = '*';
      let cronHour = '*';
      let cronMinute = '*';
      let cronSecond = '*';

      // Déterminer le type de tâche : "planned" pour exécution unique, "scheduled" pour récurrence
      let taskType = 'scheduled';

      switch (taskData.frequence) {
        case 'once':
          // Une seule fois : type "planned"
          taskType = 'planned';
          cronYear = String(startDate.getFullYear());
          cronMonth = String(startDate.getMonth() + 1);
          cronDay = String(startDate.getDate());
          // Pour une exécution unique, si aucune date de fin saisie, on aligne la fin sur le début
          if (!taskData.end_date) {
            endDate = startDate;
          }
          // Facultatif: capturer l'heure/minute/second de la fréquence si fournie
          if (taskData.frequence_time) {
            const [h, m] = taskData.frequence_time.split(':');
            if (h) cronHour = h.padStart(2, '0');
            if (m) cronMinute = m.padStart(2, '0');
            cronSecond = '0';
          }
          break;
        case 'yearly':
          cronYear = '1';
          break;
        case 'monthly':
          cronMonth = '1';
          break;
        case 'weekly':
          cronWeek = '1';
          break;
        case 'daily':
          cronDay = '1';
          break;
        case 'hourly':
          cronHour = '1';
          break;
        case 'days_of_week':
          // Jours de la semaine : utiliser cron_day_of_week
          cronDayOfWeek = taskData.days_of_week || '*';
          // Capturer l'heure si fournie
          if (taskData.frequence_time) {
            const [h, m] = taskData.frequence_time.split(':');
            if (h) cronHour = h.padStart(2, '0');
            if (m) cronMinute = m.padStart(2, '0');
            cronSecond = '0';
          }
          break;
        case 'custom': {
          const y = parseInt(taskData.interval_years, 10) || 0;
          const mo = parseInt(taskData.interval_months, 10) || 0;
          const w = parseInt(taskData.interval_weeks, 10) || 0;
          const d = parseInt(taskData.interval_days, 10) || 0;
          const h = parseInt(taskData.interval_hours, 10) || 0;
          const m = parseInt(taskData.interval_minutes, 10) || 0;
          const s = parseInt(taskData.interval_seconds, 10) || 0;
          cronYear = y > 0 ? String(y) : '*';
          cronMonth = mo > 0 ? String(mo) : '*';
          cronWeek = w > 0 ? String(w) : '*';
          cronDay = d > 0 ? String(d) : '*';
          cronHour = h > 0 ? String(h) : '*';
          cronMinute = m > 0 ? String(m) : '*';
          cronSecond = s > 0 ? String(s) : '*';
          break;
        }
        default:
          // Par défaut : tous les champs à '*'
          break;
      }

      task = {
        name: taskData.nom,
        type: taskType,
        function_id: taskData.fonction?.function_id,
        group_taches: planningContext.group_taches || 'tasks_group',
        cron_year: cronYear,
        cron_month: cronMonth,
        cron_day: cronDay,
        cron_week: cronWeek,
        cron_day_of_week: cronDayOfWeek,
        cron_hour: cronHour,
        cron_minute: cronMinute,
        cron_second: cronSecond,
        cron_start_day: formatDate(startDate),
        cron_end_date: formatDate(endDate),
        project_id: planningContext.project_id
          ? parseInt(planningContext.project_id, 10)
          : undefined,
        view_id: planningContext.view_id
          ? parseInt(planningContext.view_id, 10)
          : undefined,
        state: taskData.active ? 1 : 0,
        id_arguments: (taskData.fonction?.arguments || []).map((arg) => ({
          Position_in_arg: arg.position,
          Value: arg.valeurParDefaut || arg.value || '',
        })),
      };
    }

    // Add task_id for edit mode
    if (isEditMode && taskData.id) {
      task.task_id = taskData.id;
    }
    sauvegarderTache(task, isEditMode);
  };

  const headerTitle = readOnly
    ? 'Détails de la tâche'
    : isEditMode
      ? 'Modifier la tâche'
      : 'Créer une tâche';

  return (
    <div className={S.container}>
      <div className={S.header}>
        <h3 className={S.titre}>{headerTitle}</h3>
      </div>

      <div className={S.contenu}>
        <Collapse expandText="Informations de la tâche">
          <TaskBasicInfo
            nom={taskData.nom}
            erreur={erreurs.nom}
            onChange={readOnly ? () => {} : handleTaskChange}
            readOnly={readOnly}
          />
          <TaskPlanningSection
            projectId={planningContext.project_id}
            groupTaches={planningContext.group_taches}
            onChange={readOnly ? () => {} : handlePlanningChange}
            readOnly={readOnly}
          />
        </Collapse>

        <Collapse expandText="Exécution">
          <TaskExecutionSection
            active={taskData.active}
            mode={taskData.mode}
            frequence={taskData.frequence}
            intervalYears={taskData.interval_years}
            intervalMonths={taskData.interval_months}
            intervalWeeks={taskData.interval_weeks}
            intervalDays={taskData.interval_days}
            intervalHours={taskData.interval_hours}
            intervalMinutes={taskData.interval_minutes}
            intervalSeconds={taskData.interval_seconds}
            frequenceDate={taskData.frequence_date}
            frequenceTime={taskData.frequence_time}
            endDate={taskData.end_date}
            endTime={taskData.end_time}
            daysOfWeek={taskData.days_of_week}
            onChange={readOnly ? () => {} : handleTaskChange}
            readOnly={readOnly}
          />
        </Collapse>

        <Collapse expandText="Fonction de la tâche">
          <TaskFunctionSection
            fonction={taskData.fonction}
            availableFunctions={availableFunctions}
            loadingFunctions={loadingFunctions}
            erreur={erreurs.fonction}
            projectId={planningContext.project_id}
            onFunctionChange={readOnly ? () => {} : handleFonctionChange}
            onArgumentChange={readOnly ? () => {} : handleArgumentChange}
            readOnly={readOnly}
          />
        </Collapse>
      </div>

      {!readOnly && (
        <div className={S.actions}>
          <Bouton
            type={type_btn.secondary}
            cliquer={annuler}
            className={`${S.btnAction} ${S.btnCancel}`}
          >
            <BiX size={16} /> Annuler
          </Bouton>
          <Bouton
            type={type_btn.success}
            cliquer={handleSave}
            className={`${S.btnAction} ${S.btnSave}`}
          >
            <BiSave size={16} /> {isEditMode ? 'Modifier' : 'Créer'}
          </Bouton>
        </div>
      )}
    </div>
  );
};

TaskBuilder.propTypes = {
  initialTask: PropTypes.shape({
    task_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
    state: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    function_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    function_name: PropTypes.string,
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    view_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    group_taches: PropTypes.string,
    cron_year: PropTypes.string,
    cron_month: PropTypes.string,
    cron_week: PropTypes.string,
    cron_day: PropTypes.string,
    cron_hour: PropTypes.string,
    cron_minute: PropTypes.string,
    cron_second: PropTypes.string,
    cron_start_day: PropTypes.string,
    cron_end_date: PropTypes.string,
    id_arguments: PropTypes.arrayOf(
      PropTypes.shape({
        Position_in_arg: PropTypes.number,
        Value: PropTypes.string,
      })
    ),
  }),
  isEditMode: PropTypes.bool,
  annuler: PropTypes.func.isRequired,
  sauvegarderTache: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

TaskBuilder.defaultProps = {
  initialTask: {},
  isEditMode: false,
  readOnly: false,
};

export default React.memo(TaskBuilder);
